import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Users, MessageSquare, AlertTriangle, Settings, Search, ArrowUpRight, ArrowDownRight, MoreHorizontal, Loader2 } from 'lucide-react';
import { cn, getRoomPath } from '../utils';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { canManagePlatform } from '../utils/permissions';

const StatCard = ({ title, value, change, trend, icon: Icon, color, to, loading }) => {
  const Card = (
    <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex flex-col items-center md:items-start text-center md:text-left h-full">
      <div className="flex justify-between items-start mb-4 w-full">
        <div className={cn("p-3 rounded-2xl text-white shadow-lg", color)}>
          <Icon size={24} />
        </div>
        {!loading && change && (
          <div className={cn(
            "flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg uppercase tracking-[0.15em]",
            trend === 'up' ? "text-green-600 bg-green-50" : "text-red-600 bg-red-50"
          )}>
            {trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {change}
          </div>
        )}
      </div>
      <h3 className="text-gray-400 font-bold text-xs uppercase tracking-[0.15em] mb-1">{title}</h3>
      {loading ? (
        <Loader2 size={24} className="animate-spin text-gray-200" />
      ) : (
        <p className="text-3xl font-bold text-gray-900 tracking-tight">{value}</p>
      )}
    </div>
  );

  return to ? <Link to={to} className="block transition-transform hover:scale-[1.02]">{Card}</Link> : Card;
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [monitoring, setMonitoring] = useState([]);
  const [health, setHealth] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && !canManagePlatform(user)) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const [statsData, monitorData, healthData] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/monitoring'),
          api.get('/admin/health'),
        ]);
        setStats(statsData);
        setMonitoring(Array.isArray(monitorData) ? monitorData : []);
        setHealth(Array.isArray(healthData) ? healthData : []);
      } catch (err) {
        console.error('Failed to fetch admin data', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <StatCard 
          title="Global Participants" 
          value={stats?.participants?.toLocaleString() || '0'} 
          change="12%" 
          trend="up" 
          icon={Users} 
          color="bg-brand-indigo shadow-brand-indigo/20" 
          to="/admin/users"
          loading={loading}
        />
        <StatCard 
          title="Active Sessions" 
          value={stats?.active_sessions?.toString() || '0'} 
          change="5%" 
          trend="up" 
          icon={Shield} 
          color="bg-purple-500 shadow-purple-100" 
          to="/admin/activities"
          loading={loading}
        />
        <StatCard 
          title="Interaction Rate" 
          value={`${stats?.interaction_rate || 0}%`} 
          change="3%" 
          trend="down" 
          icon={MessageSquare} 
          color="bg-green-500 shadow-green-100" 
          to="/admin/analytics"
          loading={loading}
        />
        <StatCard 
          title="Moderation Alerts" 
          value={stats?.moderation_alerts?.toString() || '0'} 
          change="24%" 
          trend="up" 
          icon={AlertTriangle} 
          color="bg-red-500 shadow-red-100" 
          to="/admin/moderation"
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Recent Alerts */}
        <div className="lg:col-span-8">
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
            <div className="p-8 border-b border-gray-50 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
              <div>
                <h2 className="text-xl font-bold text-gray-900 tracking-tight">Active Live Monitoring</h2>
                <p className="text-xs text-gray-400 font-bold uppercase mt-1">Real-time room & participation oversight</p>
              </div>
              <Link to="/admin/activities" className="text-sm font-bold text-brand-indigo uppercase tracking-[0.15em] hover:underline">Manage All Rooms</Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[600px]">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="px-8 py-4 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Active Room</th>
                    <th className="px-8 py-4 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Type</th>
                    <th className="px-8 py-4 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Status</th>
                    <th className="px-8 py-4 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="px-8 py-20 text-center">
                        <Loader2 size={32} className="animate-spin mx-auto text-gray-200" />
                      </td>
                    </tr>
                  ) : (Array.isArray(monitoring) && monitoring.length > 0) ? (
                    monitoring.map((room, i) => (
                      <tr key={room.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-8 py-5">
                          <p className="font-bold text-gray-900 text-sm">{room.title}</p>
                          <p className="text-xxs text-gray-400 font-bold uppercase">{room.participants} participants</p>
                        </td>
                        <td className="px-8 py-5">
                          <span className={cn(
                            "text-xxs font-bold px-2 py-1 rounded-lg uppercase tracking-[0.15em]",
                            room.type === 'Q&A' ? "text-purple-600 bg-purple-50" : 
                            room.type === 'Brainstorm' ? "text-green-600 bg-green-50" : "text-brand-indigo bg-brand-indigo/5"
                          )}>
                            {room.type}
                          </span>
                        </td>
                        <td className="px-8 py-5 text-sm font-bold text-gray-900">
                          <span className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                            {room.status}
                          </span>
                        </td>
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-2">
                            <Link to={getRoomPath(room)} className="p-2 bg-gray-50 text-gray-400 hover:text-brand-indigo rounded-xl transition-all" title="View Room">
                              <Shield size={16} />
                            </Link>
                            <button className="p-2 bg-gray-50 text-gray-400 hover:text-red-600 rounded-xl transition-all" title="Quick Action">
                              <AlertTriangle size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-8 py-10 text-center text-gray-400 text-xs font-bold uppercase tracking-[0.15em]">
                        No active sessions to monitor
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="lg:col-span-4">
          <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm flex flex-col items-center md:items-start text-center md:text-left">
            <h2 className="text-xl font-bold text-gray-900 tracking-tight mb-6">Realtime Health</h2>
            <div className="space-y-6 w-full">
              {loading ? (
                <div className="py-10 flex justify-center">
                  <Loader2 size={32} className="animate-spin text-gray-200" />
                </div>
              ) : health.map((service) => (
                <div key={service.label}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-bold text-gray-900">{service.label}</span>
                    <span className={cn("text-xxs font-bold uppercase tracking-[0.15em]", service.status === 'Operational' ? "text-green-600" : "text-orange-600")}>
                      {service.status}
                    </span>
                  </div>
                  <div className="h-2 w-full bg-gray-50 rounded-full overflow-hidden">
                    <div 
                      className={cn("h-full transition-all duration-1000", service.health > 95 ? "bg-green-500" : "bg-orange-500")}
                      style={{ width: `${service.health}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
            
            <Link 
              to="/admin/settings"
              className="w-full mt-8 bg-gray-900 text-white font-bold py-4 rounded-2xl shadow-xl shadow-gray-200 flex items-center justify-center gap-2 hover:bg-black transition-all active:scale-[0.98]"
            >
              Platform Settings
              <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
